import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { CheckCircle2, HelpCircle, Info, AlertCircle, User, ChevronDown, Quote, ArrowRight, Plus, X, Calendar, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const StressAccordionItem = ({ title, icon: Icon, children, isOpen, onClick }) => {
  return (
    <div className="bg-white rounded-2xl border border-[#E8DDCE] overflow-hidden shadow-sm h-fit">
      <button
        onClick={onClick}
        className="flex w-full items-center justify-between p-6 text-left hover:bg-[#F4EFE7]/30 transition-colors group cursor-pointer"
      >
        <span className="text-xl font-serif text-[#123F13] flex items-center gap-3">
          <Icon className="text-[#8C2F12] w-6 h-6" /> {title}
        </span>
        <div className="text-[#3B352F]/40 transition-transform duration-300">
          {isOpen ? <X size={20} /> : <Plus size={20} />}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="p-6 pt-0 border-t border-[#E8DDCE]/50">
              <div className="pt-4">
                {children}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Stress = () => {
  const [openItems, setOpenItems] = useState([]);
  const canonicalUrl = "https://corehypnose.dk/stress";

  const toggleItem = (value) => {
    setOpenItems(prev => 
      prev.includes(value) 
        ? prev.filter(item => item !== value)
        : [...prev, value]
    );
  };

  const treatmentSteps = [
    "Forstå hvad der stresser dig: Jeg starter med at identificere de tanke- og bekymringsmønstre, som holder dig fanget. Du får indblik i, hvorfor stressen bliver ved, selv når ydre krav forsvinder.",
    "Metakognitive teknikker: Du lærer, hvordan du stopper overbearbejdning og bekymringer. Ikke ved at ignorere dem, men ved at ændre dit forhold til dem, så de mister deres greb.",
    "Hypnose – genfind roen i kroppen: Hypnose hjælper dig med at aktivere det parasympatiske nervesystem, så kroppen kan regulere sig selv. Mange oplever fysisk lettelse allerede under første session.",
    "Enkle øvelser du kan bruge i hverdagen: Du får redskaber, som du nemt kan anvende i din dagligdag, også når presset stiger. Øvelserne hjælper dig med at holde fast i ro og klarhed.",
    "Plan for vedligeholdelse og forebyggelse: Sammen lægger vi en strategi, der forhindrer tilbagefald, og hjælper dig med at navigere i stressede perioder uden at miste balancen."
  ];

  return (
    <ServiceLayout 
      title="Behandling af stress"
      subtitle="Er du mentalt på overarbejde? Hos Core Hypnose får du hjælp til at slippe tankemylder, genvinde ro og finde tilbage til dig selv, uden lange forløb eller sygemelding."
      description={`Stress er kroppens måde at reagere på pres, overbelastning og følelsesmæssige udfordringer. Kortvarig stress kan faktisk være hjælpsom, den får dig op i gear, skærper fokus og mobiliserer energi. Men når stressen varer ved i ugevis eller måneder, bliver den en belastning, som kroppen og sindet ikke kan ryste af sig.

Langvarig stress føles ikke ens for alle, men mange oplever uro i kroppen, tankemylder, søvnbesvær og fysiske symptomer. Det er ikke dovenskab eller svaghed – det er en reel ubalance i dit nervesystem.`}
      image="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/49d8dfbc-a08b-403e-a267-3bd0d366055c.png"
      benefits={[
        "Fri af stress-loopet",
        "Stilhed i sindet",
        "Kom ud af overgear",
        "Fra kaos til klarhed"
      ]}
      content={treatmentSteps}
      showContentSection={false}
      metaTitle="Stressbehandling med hypnose & metakognitiv terapi i Valby"
      metaDescription="Føler du dig mentalt overbelastet? Få ro i sind og krop med stressbehandling hos Core Hypnose i Valby – eller online, der hvor du føler dig tryg"
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#193f10] text-white px-6 py-3 rounded-md font-bold hover:bg-[#193f10]/90 transition-all shadow-md">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-6 py-3 rounded-md font-bold hover:bg-[#faf7f2] transition-all">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </>
      }
      footer={
        <div className="text-center pb-16">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Klar til at finde ro midt i kaos?</h2>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#a33a1a] transition-all shadow-lg">
            Book din første session nu <ArrowRight size={20} />
          </Link>
        </div>
      }
    >
      <Helmet>
        <title>Stressbehandling med hypnose & metakognitiv terapi i Valby</title>
        <meta name="description" content="Føler du dig mentalt overbelastet? Få ro i sind og krop med stressbehandling hos Core Hypnose i Valby – eller online, der hvor du føler dig tryg" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Stressbehandling med hypnose & metakognitiv terapi i Valby" />
        <meta property="og:description" content="Føler du dig mentalt overbelastet? Få ro i sind og krop med stressbehandling hos Core Hypnose i Valby – eller online, der hvor du føler dig tryg" />
        <meta property="og:image" content="" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="" />
      </Helmet>
      {/* Testimonial Section - Styled like Angst page */}
      <div className="mb-20 bg-[#123F13] text-white p-8 md:p-12 rounded-2xl relative overflow-hidden">
        <Quote className="absolute top-8 right-8 text-white/10" size={120} />
        <div className="relative z-10 max-w-2xl">
          <h3 className="text-2xl font-serif mb-6 italic">“Det føltes som at få mig selv tilbage”</h3>
          <div className="text-white/80 mb-6 leading-relaxed space-y-4">
            <p>“Jeg plejede at være typen, der havde styr på tingene. Men pludselig kunne jeg ikke koncentrere mig, småting fik mig til at græde, og jeg trak mig fra mine venner. Jeg var bare… overvældet. Jeg troede, det ville gå over af sig selv, men det blev kun værre.”</p>
            <p>“Hos Core Hypnose fik jeg ikke en lang forklaring på, hvad der var galt med mig. I stedet fik jeg redskaber, der faktisk virker. Jeg begyndte at forstå, hvorfor tankerne kørte i ring, og hvordan jeg kunne skabe ro, uden at ændre hele dit liv.”</p>
          </div>
          <p className="font-bold">— Kvinde, 38 år</p>
          <p className="text-white/60 text-sm mt-4 italic">“Nu kan jeg mærke mig selv igen, og det er den største lettelse.”</p>
        </div>
      </div>

      {/* Hvorfor bliver stressen ved med at komme? */}
      <div className="mb-20">
        <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvorfor bliver stressen ved med at komme?</h2>
        <div className="text-[#3B352F]/70 text-lg leading-relaxed space-y-4">
          <p>
            Du tager fri. Skruer ned for tempoet. Prøver at slappe af. Men alligevel er kroppen på overarbejde. Hjertet banker. Tankerne kører i ring. Du vågner stadig træt. Hvorfor forsvinder stressen ikke?
          </p>
          <p>
            Det er ikke ydre krav, der fastholder din stress. Det er dine tanker om stress. Mange oplever, at selve bekymringerne om at være stresset bliver det, der holder alarmberedskabet i gang. Denne konstante indre dialog kaldes meta-bekymringer, og de holder hjernen fanget i stressens greb.
          </p>
          <p className="text-base border-l-2 border-[#8C2F12]/30 pl-4 py-1">
            Nogle oplever også, at uro, bekymring eller stress hænger sammen med relationelle mønstre og konflikter i parforholdet. Her kan <Link to="/parterapi" className="text-[#8C2F12] font-semibold hover:underline">parterapi eller relationscoaching</Link> være en relevant vej til at skabe ro og gensidig forståelse.
          </p>
          <p className="text-base border-l-2 border-[#8C2F12]/30 pl-4 py-1">
            Oplever du voldsom udmattelse, der minder om stress, men skyldes langvarigt sansepres eller social overbelastning? Det kan være tegn på autistisk burnout. Læs mere om mit <Link to="/autismevenlig-terapi" className="text-[#8C2F12] font-semibold hover:underline">autismevenlige forløb</Link>.
          </p>
        </div>
      </div>

      {/* Jeg arbejder anderledes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
        <div>
          <h2 className="text-3xl font-serif text-[#123F13] mb-6">Jeg arbejder anderledes</h2>
          <p className="text-[#3B352F]/70 mb-6">
            Hos mig får du ikke lange analyser eller standardråd. Jeg hjælper dig med at genvinde kontrollen over dine tanker, og give kroppen ro til at finde balancen igen.
          </p>
          <div className="space-y-4">
            {[
              "stopper tankemylder og selvkritik",
              "lader bekymringer passere uden at gribe fat i dem",
              "giver nervesystemet ro til at falde til"
            ].map((item, i) => (
              <div key={i} className="flex gap-3 items-start">
                <CheckCircle2 className="w-5 h-5 text-[#8C2F12] mt-1 shrink-0" />
                <span className="text-[#3B352F]/80">{item}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-[#3B352F]/70 italic">
            Du behøver ikke forstå alt. Du skal bare være nysgerrig nok til at opdage, at forandring faktisk er mulig.
          </p>
        </div>
        <div className="bg-[#F4EFE7] p-8 rounded-2xl border border-[#E8DDCE] flex flex-col justify-center items-center text-center">
          <h3 className="text-xl font-serif text-[#123F13] mb-4">Langvarig stress behøver ikke være din nye normal</h3>
          <p className="text-[#3B352F]/70">
            Lad mig vise dig, hvordan du kan bryde mønstret og få roen tilbage, uden at skulle ændre hele dit liv.
          </p>
          <Link 
            to="/bestil-tid" 
            className="inline-flex items-center gap-2 mt-6 bg-[#8C2F12] text-white px-6 py-3 rounded-full font-medium hover:bg-[#8C2F12]/90 transition-colors"
          >
            Book en session
          </Link>
        </div>
      </div>

      {/* Symptomer */}
      <div className="mb-20 max-w-5xl mx-auto">
        <h2 className="text-3xl font-serif text-[#123F13] mb-10 text-center">Typiske symptomer på stress: Sådan mærker du det</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
          {/* Fysiske tegn */}
          <StressAccordionItem 
            title="Fysiske tegn" 
            icon={AlertCircle}
            isOpen={openItems.includes('physical')}
            onClick={() => toggleItem('physical')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Hjertebanken, muskelspændinger og hovedpine</strong> – kroppen spænder konstant op.</li>
              <li>• <strong>Søvnbesvær</strong> – enten svært ved at falde i søvn, hyppige opvågninger eller tidlig morgenvågning.</li>
              <li>• <strong>Reaktionsdygtighed</strong> – konstant øget alarmberedskab, selv ved små irritationer.</li>
              <li>• <strong>Fordøjelsesproblemer</strong> – såsom diarré, forstoppelse, mavegener eller kvalme.</li>
              <li>• <strong>Trætired og mangel på energi</strong> – selv efter lange nætter med søvn.</li>
            </ul>
          </StressAccordionItem>

          {/* Mentale symptomer */}
          <StressAccordionItem 
            title="Mentale symptomer" 
            icon={Info}
            isOpen={openItems.includes('mental')}
            onClick={() => toggleItem('mental')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Tankemylder og bekymringer</strong> – tankerne kører i ring, og du har svært ved at “slukke” for dem.</li>
              <li>• <strong>Koncentrationsbesvær</strong> – det føles umuligt at fokusere, selv på enkle opgaver.</li>
              <li>• <strong>Forringet hukommelse</strong> – du glemmer aftaler, småting eller ting du læste for kort tid siden.</li>
              <li>• <strong>Overdreven selvkritik</strong> – fx “hvad nu hvis jeg fejler?” eller “hvad nu hvis…”.</li>
            </ul>
          </StressAccordionItem>

          {/* Følelsesmæssige reaktioner */}
          <StressAccordionItem 
            title="Følelsesmæssige reaktioner" 
            icon={AlertCircle}
            isOpen={openItems.includes('emotional')}
            onClick={() => toggleItem('emotional')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Irritabilitet og kort lunte</strong> – du reagerer hurtigere og kraftigere end normalt.</li>
              <li>• <strong>Følelse af tomhed</strong> – det føles svært at finde glæde i det, der plejede at give mening.</li>
              <li>• <strong>Angst for stressen selv</strong> – du er bange for dine symptomer og hvad der kan ske.</li>
            </ul>
          </StressAccordionItem>

          {/* Adfærd og vaner */}
          <StressAccordionItem 
            title="Adfærd og vaner" 
            icon={Info}
            isOpen={openItems.includes('behavior')}
            onClick={() => toggleItem('behavior')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Social tilbagetrækning</strong> – du har ikke overskud til andre.</li>
              <li>• <strong>Undgåelsesadfærd</strong> – udskyder opgaver eller holder dig væk fra stresskilder.</li>
              <li>• <strong>“Junk coping”</strong> – spise mere, drikke mere kaffe eller alkohol, ryge mere.</li>
              <li>• <strong>Rastløshed</strong> – du kan ikke sidde stille og har konstant behov for “noget at lave”.</li>
            </ul>
          </StressAccordionItem>
        </div>
      </div>

      {/* Effektiv behandling */}
      <div className="mb-20">
        <h2 className="text-3xl font-serif text-[#123F13] mb-6">Effektiv og evidensbaseret stressbehandling – derfor virker den</h2>
        <p className="text-[#3B352F]/70 mb-8">
          Måske har du allerede forsøgt at skrue ned for opgaver, tage en pause eller blive sygemeldt, men oplever stadig, at tankemylderet og uro i kroppen ikke forsvinder. Det er ikke dig, der fejler noget. Det er strategien, der skal ændres.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-xl font-serif text-[#123F13] mb-4">Jeg arbejder med det, der holder stressen i live</h3>
            <p className="text-[#3B352F]/70 mb-6">
              Hos mig handler stressbehandling ikke om at grave i fortiden. I stedet fokuserer vi på her-og-nu – hvor dine tanker, bekymringer og selvfokus kan få stresssymptomerne til at vedblive.
            </p>
            <div className="space-y-4">
              {[
                { title: "Metakognitiv terapi", desc: "Slip bekymringer og overtænkning." },
                { title: "Hypnose", desc: "Skab ro i kroppen og aktiver dit indre beroligende system." },
                { title: "Praktiske redskaber", desc: "Brug dem med det samme i din hverdag." }
              ].map((item, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#8C2F12] mt-1 shrink-0" />
                  <p className="text-[#3B352F]/70">
                    <strong className="text-[#123F13]">{item.title}:</strong> {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-xl font-serif text-[#123F13] mb-4">Fokus på kontrol og frihed</h3>
            <p className="text-[#3B352F]/70">
              Du lærer, at du ikke skal kæmpe mod dine tanker, men at du trygt kan lade dem være uden at handle på dem. På den måde bryder vi det mønster, der holder stressen i gang.
            </p>
            <p className="text-[#3B352F]/70 mt-4 border-l-2 border-[#8C2F12]/30 pl-4 py-1 text-sm">
              Nogle oplever også, at uro, bekymring eller stress hænger sammen med relationelle mønstre. Her kan <Link to="/parterapi" className="text-[#8C2F12] font-semibold hover:underline">parterapi eller relationscoaching</Link> være relevant.
            </p>
          </div>
        </div>
      </div>

      {/* Hvordan foregår behandlingen sektion - Flyttet hertil */}
      <div className="mb-20">
        <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-10">Hvordan foregår behandlingen?</h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {treatmentSteps.map((item, index) => {
            const hasColon = item.includes(':');
            const titlePart = hasColon ? item.split(':')[0].trim() : `Trin ${index + 1}`;
            const descPart = hasColon ? item.split(':').slice(1).join(':').trim() : item;
            
            // Dynamisk layout baseret på antal punkter
            const totalItems = treatmentSteps.length;
            let colSpanClasses = "";

            if (totalItems === 4) {
              colSpanClasses = "md:col-span-6 lg:col-span-3";
            } else if (totalItems === 5) {
              colSpanClasses = index >= 3 ? "md:col-span-6 lg:col-span-6" : "md:col-span-4 lg:col-span-4";
            } else if (totalItems === 3) {
              colSpanClasses = "md:col-span-4 lg:col-span-4";
            } else {
              colSpanClasses = "md:col-span-6 lg:col-span-4";
            }
            
            return (
              <div 
                key={index} 
                className={`bg-white p-8 rounded-2xl border border-[#E8DDCE] shadow-sm hover:shadow-md transition-all group relative overflow-hidden ${colSpanClasses}`}
              >
                <div className="absolute -top-4 -right-4 text-6xl font-serif text-[#123F13]/5 font-bold group-hover:text-[#8C2F12]/10 transition-colors">
                  {(index + 1).toString().padStart(2, '0')}
                </div>
                <div className="relative z-10">
                  <span className="text-[#8C2F12] font-bold text-sm mb-2 block">{(index + 1).toString().padStart(2, '0')}</span>
                  <h3 className="text-xl font-bold text-[#123F13] mb-3">{titlePart}</h3>
                  <p className="text-[#3B352F]/70 leading-relaxed">
                    {descPart}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ServiceLayout>
  );
};

export default Stress;
